import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const url = originalRequest.url || '';
    const isAuthEndpoint = url.includes('/auth/login') ||
                           url.includes('/auth/register') ||
                           url.includes('/auth/refresh') ||
                           url.includes('/auth/me');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest)).catch(() => Promise.reject(error));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const { data } = await axios.post(
          `${API_URL}/api/auth/refresh`,
          { refresh_token: refreshToken },
          { withCredentials: true }
        );
        if (data?.access_token) {
          localStorage.setItem('access_token', data.access_token);
        }
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        processQueue(refreshError);
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Helper to format API error
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Styles
export const stylesApi = {
  getAll: () => api.get('/styles'),
  get: (styleKey) => api.get(`/styles/${styleKey}`),
};

// Projects
export const projectsApi = {
  getAll: () => api.get('/projects'),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getScreenplay: (id) => api.get(`/projects/${id}/screenplay`),
  getScreenplayText: (id, act = null) => api.get(`/projects/${id}/screenplay/text`, { params: act ? { act } : {} }),
  validateScreenplay: (id) => api.post(`/projects/${id}/screenplay/validate`),
  diagnose: (id) => api.post(`/projects/${id}/diagnose`),
  getDiagnosisHistory: (id) => api.get(`/projects/${id}/diagnosis-history`),
  getScreenplayStats: (id) => api.get(`/projects/${id}/screenplay/stats`),
  getDirectives: (id) => api.get(`/projects/${id}/directives`),
  updateDirectives: (id, directives) => api.put(`/projects/${id}/directives`, { directives }),
  getCharacterArc: (id) => api.get(`/projects/${id}/character-arc`),
  updateCharacterArc: (id, arc) => api.put(`/projects/${id}/character-arc`, arc),
  getFoundation: (id) => api.get(`/projects/${id}/film-foundation`),
  updateFoundation: (id, data) => api.put(`/projects/${id}/film-foundation`, data),
  validateFoundation: (id, data) => api.post(`/projects/${id}/film-foundation/validate`, data),
  updateProject: (id, data) => api.put(`/projects/${id}`, data),
};

// Story Answers
export const storyAnswersApi = {
  get: (projectId) => api.get(`/projects/${projectId}/story-answers`),
  update: (projectId, data) => api.put(`/projects/${projectId}/story-answers`, data),
};

// Characters
export const charactersApi = {
  getAll: (projectId) => api.get(`/projects/${projectId}/characters`),
  create: (projectId, data) => api.post(`/projects/${projectId}/characters`, data),
  delete: (projectId, characterId) => api.delete(`/projects/${projectId}/characters/${characterId}`),
};

// Beats
export const beatsApi = {
  getAll: (projectId) => api.get(`/projects/${projectId}/beats`),
  create: (projectId, data) => api.post(`/projects/${projectId}/beats`, data),
  update: (projectId, beatId, data) => api.put(`/projects/${projectId}/beats/${beatId}`, data),
  delete: (projectId, beatId) => api.delete(`/projects/${projectId}/beats/${beatId}`),
  generateB2: (projectId) => api.post(`/projects/${projectId}/generate/beat-sheet-b2`),
  lock: (projectId) => api.post(`/projects/${projectId}/beats/lock`),
  unlock: (projectId) => api.post(`/projects/${projectId}/beats/unlock`),
};

// Scenes
export const scenesApi = {
  getAll: (projectId) => api.get(`/projects/${projectId}/scenes`),
  get: (projectId, sceneId) => api.get(`/projects/${projectId}/scenes/${sceneId}`),
  create: (projectId, data) => api.post(`/projects/${projectId}/scenes`, data),
  update: (projectId, sceneId, data) => api.put(`/projects/${projectId}/scenes/${sceneId}`, data),
  delete: (projectId, sceneId) => api.delete(`/projects/${projectId}/scenes/${sceneId}`),
  getVersions: (projectId, sceneId) => api.get(`/projects/${projectId}/scenes/${sceneId}/versions`),
  rewrite: (projectId, sceneId, data) => api.post(`/projects/${projectId}/scenes/${sceneId}/rewrite`, data),
  deepRewrite: (projectId, sceneId, data) => api.post(`/projects/${projectId}/scenes/${sceneId}/deep-rewrite`, data),
  diagnose: (projectId, sceneId) => api.post(`/projects/${projectId}/scenes/${sceneId}/diagnose`),
  regenerate: (projectId, sceneId) => api.post(`/projects/${projectId}/scenes/${sceneId}/regenerate`),
};

// Generation
export const generateApi = {
  premise: (projectId) => api.post(`/projects/${projectId}/generate/premise`),
  actStructure: (projectId) => api.post(`/projects/${projectId}/generate/act-structure`),
  beatSheet: (projectId) => api.post(`/projects/${projectId}/generate/beat-sheet`),
  sceneList: (projectId) => api.post(`/projects/${projectId}/generate/scene-list`),
  screenplay: (projectId) => api.post(`/projects/${projectId}/generate/screenplay`),
  rewriteAct: (projectId, act, data) => api.post(`/projects/${projectId}/acts/${act}/rewrite`, data),
  regenerateScreenplay: (projectId, data) => api.post(`/projects/${projectId}/regenerate-screenplay`, data),
  // New: background generation with progress
  startGeneration: (projectId, data) => api.post(`/projects/${projectId}/generation/start`, data),
  getGenerationStatus: (projectId) => api.get(`/projects/${projectId}/generation/status`),
  dismissGeneration: (projectId) => api.delete(`/projects/${projectId}/generation/dismiss`),
  cancelGeneration: (projectId) => api.post(`/projects/${projectId}/generation/cancel`),
  generateAll: (projectId) => api.post(`/projects/${projectId}/generate-all`),
};

// Export URLs
export const exportApi = {
  fountain: (projectId) => `${API_URL}/api/projects/${projectId}/export/fountain`,
  fdx: (projectId) => `${API_URL}/api/projects/${projectId}/export/fdx`,
  text: (projectId) => `${API_URL}/api/projects/${projectId}/export/text`,
  pdf: (projectId) => `${API_URL}/api/projects/${projectId}/export/pdf`,
  devPackage: (projectId) => `${API_URL}/api/projects/${projectId}/export/dev-package`,
  mmswInfo: (projectId) => api.get(`/projects/${projectId}/export/mmsw-info`),
};

// Project Format Types
export const PROJECT_FORMATS = [
  { key: "feature_film", label: "Feature Film", description: "90-120 pages, lean first-draft structure", pageRange: [90, 120], defaultPages: 90, presets: [90, 100, 110, 120] },
  { key: "short_film", label: "Short Film", description: "5-20 pages, tight focused structure", pageRange: [5, 20], defaultPages: 15 },
];

// Style definitions for frontend (mirrors backend)
export const STYLE_CATEGORIES = {
  classic: {
    name: "Classic & Influential",
    styles: {
      wilder: { name: "Sharp Structure & Wit", description: "Sharp structure, irony, elegant dialogue, sophisticated comedy" },
      coen: { name: "Quirky Character-Driven", description: "Quirky, character-driven, dark humour, offbeat rhythm" },
      kaufman: { name: "Existential & Surreal", description: "Existential, surreal, introspective, meta-narrative" },
      towne: { name: "Layered Moral Tension", description: "Clean structure, moral tension, layered exposition" },
      tarantino: { name: "Stylized Dialogue", description: "Stylised dialogue, tension, character voice, long-form exchanges" },
      goldman: { name: "Clarity & Pacing", description: "Clarity, pacing, audience engagement, commercial appeal" },
      coppola: { name: "Operatic & Epic", description: "Operatic tone, family dynamics, power structures" },
    }
  },
  contemporary: {
    name: "Contemporary & Acclaimed",
    styles: {
      peele: { name: "Social Thriller", description: "High-concept, social tension, slow-burn suspense" },
      sorkin: { name: "Rhythmic Dialogue", description: "Fast-paced, intelligent, rhythmic dialogue, walk-and-talk" },
      nolan: { name: "Conceptual Non-Linear", description: "Non-linear structure, conceptual storytelling, puzzle-box narrative" },
      anderson: { name: "Precise & Stylized", description: "Precise, stylised, symmetrical, dry humour, deadpan" },
      schrader: { name: "Psychological Depth", description: "Psychological depth, internal conflict, moral isolation" },
    }
  },
  genre: {
    name: "Genre & Golden Age",
    styles: {
      hecht: { name: "Fast Wit & Energy", description: "Fast dialogue, wit, newsroom energy, screwball pacing" },
      brackett: { name: "Noir Character Interplay", description: "Noir tone, sharp character interplay, atmospheric tension" },
      lehman: { name: "Clean Suspense", description: "Thriller structure, clean suspense mechanics, escalating tension" },
      morgan: { name: "High-Energy Action", description: "High-energy, action-driven, accessible storytelling, blockbuster pacing" },
      hatten: { name: "Kinetic Modern Action", description: "Modern action, kinetic pacing, stylised sequences, visual storytelling" },
    }
  }
};

export const AVAILABLE_STYLES = {
  original: { name: "Original", description: "Neutral cinematic style" },
  ...STYLE_CATEGORIES.classic.styles,
  ...STYLE_CATEGORIES.contemporary.styles,
  ...STYLE_CATEGORIES.genre.styles,
};

export const STYLE_STRENGTHS = [
  { key: "subtle", label: "Subtle", description: "Light influence, barely noticeable" },
  { key: "balanced", label: "Balanced", description: "Noticeable but natural influence" },
  { key: "strong", label: "Strong", description: "Prominent stylistic presence" },
];

export const tokenApi = {
  getBalance: () => api.get('/tokens/balance'),
  getUsage: (limit = 50) => api.get(`/tokens/usage?limit=${limit}`),
  getPending: () => api.get('/tokens/pending'),
  addTokens: (amount) => api.post('/tokens/add', { amount }),
  getPacks: () => api.get('/tokens/packs'),
  createCheckout: (pack_id, origin_url) => api.post('/tokens/purchase', { pack_id, origin_url }),
  verifyPurchase: (session_id) => api.post('/tokens/verify-purchase', { session_id }),
  checkPurchaseStatus: (session_id) => api.get(`/tokens/purchase/status/${session_id}`),
};

export const accountApi = {
  getSettings: () => api.get('/account/settings'),
  deleteAccount: () => api.delete('/account'),
  cancelSubscription: () => api.post('/account/cancel-subscription'),
};

export default api;
